import { Node, Text } from 'slate';

// Zero-width space for word boundaries (works with all languages including Japanese)
const ZWSP = '\u200B';

// Serialize Slate nodes to markdown-like format that can be displayed
export const serializeSlateToMarkdown = (nodes) => {
  return nodes.map(n => serializeNode(n, null, 0)).join('\n');
};

// Check if a text node has any formatting
const hasFormatting = (node) => {
  return node.bold || node.italic || node.strikethrough || node.code;
};

// Serialize text children with proper boundaries between formatted/unformatted text
const serializeTextChildren = (children) => {
  let result = '';
  for (let i = 0; i < children.length; i++) {
    const node = children[i];
    if (!Text.isText(node)) continue;

    let text = node.text;
    if (!text) continue;

    const nodeHasFormatting = hasFormatting(node);
    const prevNode = i > 0 ? children[i - 1] : null;
    const prevHasFormatting = prevNode && Text.isText(prevNode) ? hasFormatting(prevNode) : false;

    // Add zero-width space between formatted and unformatted text
    if (i > 0 && nodeHasFormatting !== prevHasFormatting) {
      result += ZWSP;
    }

    // Apply text formatting with markdown syntax
    if (node.bold) {
      text = `*${text}*`;
    }
    if (node.italic) {
      text = `_${text}_`;
    }
    if (node.strikethrough) {
      text = `~${text}~`;
    }
    if (node.code) {
      text = `\`${text}\``;
    }
    // Underline doesn't have standard Slack markdown

    result += text;
  }
  return result;
};

const serializeNode = (node, parentType = null, listIndex = 0) => {
  if (Text.isText(node)) {
    let text = node.text;

    // Apply text formatting with markdown syntax
    if (node.bold) {
      text = `*${text}*`;
    }
    if (node.italic) {
      text = `_${text}_`;
    }
    if (node.strikethrough) {
      text = `~${text}~`;
    }
    if (node.code) {
      text = `\`${text}\``;
    }
    if (node.underline) {
      // Underline doesn't have standard markdown, keep as is or use HTML
      text = text; // Could use <u>${text}</u> if needed
    }

    return text;
  }

  switch (node.type) {
    case 'paragraph':
      // Use special handling for text children to add proper boundaries
      return serializeTextChildren(node.children);
    case 'code-block':
      const codeChildren = node.children.map(n => serializeNode(n, node.type, 0)).join('');
      return `\`\`\`${codeChildren}\`\`\``;
    case 'bulleted-list':
      return node.children.map((child, index) => serializeNode(child, 'bulleted-list', index)).join('\n');
    case 'numbered-list':
      return node.children.map((child, index) => serializeNode(child, 'numbered-list', index)).join('\n');
    case 'list-item':
      const listChildren = node.children.map(n => serializeNode(n, node.type, 0)).join('');
      // Check parent type to determine bullet or number - use Slack's native format
      if (parentType === 'numbered-list') {
        // Use actual numbers for numbered lists
        return `${listIndex + 1}. ${listChildren}`;
      } else {
        // Use bullet character for Slack compatibility
        return `• ${listChildren}`;
      }
    case 'block-quote':
      const quoteChildren = node.children.map(n => serializeNode(n, node.type, 0)).join('');
      // Add > prefix for each line in the quote
      return quoteChildren.split('\n').map(line => `> ${line}`).join('\n');
    case 'link':
      const linkText = node.children.map(n => serializeNode(n, node.type, 0)).join('');
      // Slack link format: <url|text> or just <url>
      if (linkText && linkText !== node.url) {
        return `<${node.url}|${linkText}>`;
      }
      return `<${node.url}>`;
    default:
      const defaultChildren = node.children ? node.children.map(n => serializeNode(n, node.type, 0)).join('') : '';
      return defaultChildren;
  }
};
